import { useEffect, useMemo, useRef, useState } from 'react'
import { setupWorker } from 'msw/browser'
import { HandlerConfig, HttpSettings } from './types'

/** Start Mock Service Worker with the provided config and return true when ready. */
export const useWorker = ({ startOptions, requestHandlers }: HttpSettings, config: HandlerConfig) => {
  const [isReady, setIsReady] = useState(false)
  const workerRef = useRef<ReturnType<typeof setupWorker> | null>(null)
  const isStarted = useRef(false)

  // Create enhanced config with scenario from URL (immutable approach)
  const enhancedConfig = useMemo<HandlerConfig>(
    () => ({
      ...config,
      scenario: new URLSearchParams(window.location.search).get('scenario'),
    }),
    [config]
  )

  // Store the previous config in a ref so we can compare it to the current config
  // This way, we know if the config has changed.
  const prevConfig = useRef<HandlerConfig | undefined>(undefined)

  // Initialize worker once on mount
  useEffect(() => {
    if (!isStarted.current) {
      isStarted.current = true

      try {
        workerRef.current = setupWorker(...requestHandlers(enhancedConfig))

        // Configure MSW start options to only intercept specific requests
        const mswStartOptions = {
          ...startOptions,
          // Only intercept requests that have registered handlers
          onUnhandledRequest: (request: Request) => {
            const url = new URL(request.url)

            // List of domains/patterns that should be allowed to pass through silently
            const allowedPatterns = [
              'cognito-idp', // AWS Cognito
              'amazonaws.com', // AWS services
              'localhost:3000/mockServiceWorker.js', // MSW worker script itself
            ]

            // Check if the request should be allowed to pass through
            const isAllowed = allowedPatterns.some(
              (pattern) => url.hostname.includes(pattern) || request.url.includes(pattern)
            )

            if (isAllowed) {
              // Silently allow these requests to pass through
              return 'bypass'
            }

            // For other unhandled requests, just warn (don't throw)
            if (!url.pathname.startsWith('/mockServiceWorker')) {
              // Silently bypass unhandled requests
            }
            return 'bypass' // Allow to pass through
          },
        }

        workerRef.current
          .start(mswStartOptions)
          .then(() => {
            setIsReady(true)
          })
          .catch(() => {
            // Still set ready to true so app doesn't hang
            // MSW will log warnings for unmocked requests
            setIsReady(true)
          })
      } catch {
        // Set ready after a small delay to avoid cascading render issues
        setTimeout(() => setIsReady(true), 0)
      }
    }
  }, [enhancedConfig, requestHandlers, startOptions])

  // Update handlers when config changes
  useEffect(() => {
    if (!workerRef.current || !isReady) return
    // This only needs to re-run if the config has changed so the new settings apply.
    if (prevConfig.current === enhancedConfig) return
    prevConfig.current = enhancedConfig
    workerRef.current.use(...requestHandlers(enhancedConfig))
  }, [enhancedConfig, isReady, requestHandlers])

  return isReady
}
